<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { $bus, mqtt } from '@/common/events'
import { dialog } from '@/common/dialog'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import Pagination from '@/components/Pagination.vue'
import DropDown from '@/components/DropDown.vue'
import Toast from '@/components/Toast.vue'
import Label from '@/components/Label.vue'
import TextBox from '@/components/TextBox.vue'
import Button from '@/components/Button.vue'
import EventLog from '@/components/EventLog.vue'
import Fullscreen from '@/components/Fullscreen.vue'
import AutocompleteTextBox from '@/components/AutocompleteTextBox.vue'
import InsertIndicator from '@/components/InsertIndicator.vue'
import KeyboardNumpad from '@/components/KeyboardNumpad.vue'
import { M280TCabinetControl } from '@/drivers/cabinet/PLT-M280T.js'
import { CabinetService } from "@/services/CabinetService"
import { useTranslate } from '@/composables/language'

const { t } = useTranslate();

let cabinet = null;

let cabinetService = new CabinetService();

const props = defineProps({
  cabinet: {
    type: Object,
    default: null
  },
  user: {
    type: Object,
    default: null
  },
});

const emit = defineEmits([ "onLog" ]);
function insertLog(record) {
  emit("onLog", record);
}

const mac = computed(() => {
  return props.cabinet?.mac;
});

const ip = computed(() => {
  if (props.cabinet) {
    let type = props.cabinet.protocol || props.cabinet.type || "";
    let host = props.cabinet.host;
    let port = props.cabinet.port;
    return `${type} ${host}:${port}`.trim();
  }
  return null;
});

watch(mac, (newValue) => {
  initializeController();
});

watch(props.cabinet, (newValue) => {
  //console.log("watch cabinet", newValue)
});

const instance = getCurrentInstance();

const isModalVisible = ref(false);
const isOpening = ref(false);
const trayIndex = ref(1);
const trayNumber = ref();

let lastCommandRaw = null;
let lastCommandTime = null;
let insideRecurring = false;

const categories = ref([
  { name: "A" },
  { name: "B" },
  { name: "C" },
  { name: "D" },
  { name: "E" },
]);

const sortOptions = ref([
  { name: "Po abecedi" },
  { name: "Po zasedenosti" }, //Kapaciteta
]);

const suggestions = ref([
  { name: "A/0015" },
  { name: "B/0015" },
  { name: "C/0015" },
]);

const isReferenced = ref(null);
const movementStatus = ref(null);
const internalState = ref(null);
const motorStatus = ref(null);
//const motors = ref([ 'A', 'B', 'X', 'Y', 'Z', 'P' ]);
const motors = ref([ 'A', 'B', 'P', 'X', 'Y', 'Z' ]);

const isHaltAlarm = ref(null);
const isCurtainAlarm = ref(null);
const isDisruptAlarm = ref(null);

async function initializeController() {
  if (mac.value) {
    if (cabinet) {
      cabinet.dispose();
    }

    cabinet = new M280TCabinetControl({ mqtt: mqtt, mac: mac.value });

    //Get status when change occurs
    cabinet.on("status", async (data) => {
      //console.log("M280 status", data);

      if (data?.status) {
        movementStatus.value = data.status;
      }

      if (typeof data.referenced != "undefined") {
        isReferenced.value = data.referenced;
      }
      if (typeof data.internalState != "undefined") {
        internalState.value = data.internalState;
      }
      if (typeof data.motorStatus != "undefined") {
        motorStatus.value = data.motorStatus;
      }
      if (typeof data.movementStatus != "undefined") {
        movementStatus.value = data.movementStatus;
      }
      if (data.status == "error" && data.substatus == "alarm_curtain") {
        isCurtainAlarm.value = true;
      }
      if (data.status == "error" && data.substatus == "alarm_halt") {
        isHaltAlarm.value = true;
      }
      if (data.status == "error" && data.substatus == "disrupt") {
        isDisruptAlarm.value = true;
      }
      //applyStatus(data);
    });

    //Get last status now
    //applyStatus(await hcu.cabinet(mac.value).getStatus2());
  }
}


let isTrayOpen = null;
function openChanged(newValue) {
  if (newValue == true) {
    isOpening.value = false;
  }
  //alert("Tray changed: " + newValue);

  if (newValue == true) {
    dialog({
      type: "success",
      //title: "Potrditev",
      text: t('service.tray_opened'),
      confirmButtonText: t('generic.ok'),
      //cancelButtonText: "Prekliči",
    });
  }
}

const eventLogElement = ref();
const eventLogHeight = ref(300);

function calculateEventLogHeight() {
  //Calculate event log height
  let element = document.getElementById("eventLog");
  if (element) {
    let offset = element.getBoundingClientRect(); //Get element position within the window
    eventLogHeight.value = window.innerHeight - offset.top - 20;
  }
}

onMounted(async () => {
  reset();

  initMQTT();

  await initializeController();
  calculateEventLogHeight();
});

onUnmounted(() => {
  if (cabinet) {
    cabinet.dispose();
  }
});

function effectiveUnits(inserts) {
  let list = [];
  let parts = inserts.split(/,/g);
  let count = 0;
  for (let i = 0; i < parts.length; i++) {
    let part = parts[i];
    let len = parseInt(part.replace(/[^\d]+/g, ""));
    let empty = part.includes("?");
    if (!empty) ++count;
  }
  return count;
}

function highlightInsert(inserts, index) {
  let list = [];
  let result = "";
  let parts = inserts.split(/,/g);
  let count = 0;
  for (let i = 0; i < parts.length; i++) {
    let part = parts[i];
    let len = parseInt(part.replace(/[^\d]+/g, ""));
    let empty = part.includes("?");
    if (!empty) ++count;
    let unit = !empty ? (index == count ? len : (len + "x")) : (len + "?");
    list.push(unit);
  }
  result = list.join(",");
  return result;
}

function showModal(i) {
  trayIndex.value = i;
  isModalVisible.value = true;
  isOpening.value = false;
}

function closeModal() {
  isModalVisible.value = false;
}

function getArguments() {
  let number = trayNumber.value;

  let outputWindow = selectedOutputWindow.value !== null ? selectedOutputWindow.value : 1;

  let curtainPredefined = selectedCurtainPredefined.value;
  if (curtainPredefined) {
    let result = {
      trayNumber: number,
      curtainPredefined: curtainPredefined.value,
      outputWindow
    };

    return result;
  }
  else {
    //Curtain 0-100
    let curtainStart = ((selectedCurtainStart.value || 1) - 1) * 10;
    let curtainStop = (selectedCurtainStop.value || 10) * 10;

    //Check if selected in reverse order from right to left
    if (curtainStop < curtainStart) {
      curtainStop = selectedCurtainStart.value; //Reverse
      curtainStart = selectedCurtainStop.value; //Reverse
    }

    //Keep values inside the bounds
    if (!curtainStart) {
      curtainStart = 0;
      curtainStop = 100;
    }
    else if (!curtainStop || curtainStart > curtainStop) {
      curtainStop = 100;
    }

    //Calculate curtain opening length
    let curtainLength = curtainStop - curtainStart;

    let result = {
      trayNumber: number,
      curtainStart,
      curtainLength,
      outputWindow
    };

    return result;
  }
}

async function openTray(options) {
  lastAction.value = "deliver";

  try {
    if (!trayNumber.value) {
      throw new Error(t('service.enter_tray_number'));
    }

    let args = getArguments();

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    //logRecords.value.push({ text: `${doorNumber} ${drumPosition}`, type: "command", direction: "outbound", time: time });
    //let data = await cabinetService.openDrum({ drumPosition, doorNumber });

    try {
      let trayName = "" + trayNumber.value;
      //args.serviceWindow
      insertLog({
        uid: props.user?.id,
        tn: trayName,
        cid: props.cabinet?.id,
        msg: `Servisno okno: odpiranje predala ${trayName}, pozicija zavese ${args.curtainStart}, dolžina odprtine ${args.curtainLength}` + (props.cabinet ? " na omari " + (props.cabinet?.name || "???") : ""),
        ts: new Date()
      });
    }
    catch (err) {
    }

    //socket.emit("publish", { topic: "hcu/cabinet/drum/open", payload:{ drumPosition, doorNumber } });
    //mqtt.publish({ topic: "hcu/cabinets/" + mac.value + "/open", payload: { drumPosition, doorNumber } });
    await cabinet.openTray(args, options);

    //movementStatus.value = "dostavljam...";
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);

    movementStatus.value = "napaka";
  }
}

async function closeTray(options) {
  lastAction.value = "store";

  try {
    if (!trayNumber.value) {
      throw new Error(t('service.enter_tray_number'));
    }

    let args = { trayNumber: trayNumber.value };

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;

    try {
      let trayName = "" + trayNumber.value;
      insertLog({
        uid: props.user?.id,
        tn: trayName,
        cid: props.cabinet?.id,
        msg: `Servisno okno: zapiranje zadnjega odprtega predala ${trayName}` + (props.cabinet ? " na omari " + (props.cabinet?.name || "???") : ""),
        ts: new Date()
      });
    }
    catch (err) {
    }
    
    await cabinet.closeTray(args);

    //movementStatus.value = "pospravljam...";
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);

    movementStatus.value = "napaka";
  }
}

async function stopAction() {
  lastAction.value = "idle";

  try {
    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    
    await cabinet.stopAction();

    //movementStatus.value = "v mirovanju";
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);

    movementStatus.value = "napaka";
  }
}

async function goToRef() {
  try {
    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    
    await cabinet.goToRef();

    //movementStatus.value = "referenciram...";
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);

    movementStatus.value = "napaka";
  }
}

async function set800() {
  try {
    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    
    await cabinet.requestClearEEPROM();
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);
  }
}

async function promptSet800() {
  let result = await dialog({
    type: "question-danger",
    title: t('service.confirm_title'),
    text: t('service.clear_eeprom_text'),
    confirmButtonText: t('generic.clear'),
    cancelButtonText: t('generic.cancel'),
  });

  if (result && result.confirm) {
    try {
      await set800();
    }
    catch (err) {
      console.error(err);
      dialog(err);
    }
  }
}

function submit() {
  if (lastAction.value == "deliver") {
    closeTray(true);
  }
  else {
    openTray();
  }
}

const logRecords = ref([
  /*
  { text: "90,4-7", time: "13:23:00", type: "command", direction: "outbound" },
  { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+12ms" },
  { text: "DONE", time: "13:23:07", type: "success", direction: "inbound", elapsed: "+7245ms" },
  { text: "170,1-10", time: "13:23:10", type: "command", direction: "outbound" },
  { text: "ERR", time: "13:23:10", type: "danger", direction: "inbound" },
  */
]);

const lastAction = ref(null);

const currentTrayNumber = ref(null);
const currentCurtainPosition = ref(null);

const selectedTrayNumber = ref(null);
const selectedCurtainPosition = ref(null);
const selectedRow = ref(2);
const selectedColumn = ref(3);
const selectedCurtainStart = ref(null);
const selectedCurtainStop = ref(null);
const selectedCurtainPredefined = ref(null);
let isSelectingCurtainStart = true;

function start() {
  let drum = selectedDrumPosition.value;
  let door = selectedDoorNumber.value;
  openDrum(drum, door);
}

function reset() {
  isOpening.value = false;
  //currentDrumPosition.value = null;
  //currentDoorNumber.value = null;
  //selectedDrumPosition.value = null;
  //selectedDoorNumber.value = null;
  logRecords.value = [ ];
}

function selectRow(i) {
  selectedRow.value = i;
}

function selectColumn(i) {
  selectedColumn.value = i;
}

function selectCurtain(i) {
  if (typeof i == "object") {
    selectedCurtainPredefined.value = i;
    selectedCurtainStart.value = null;
    selectedCurtainStop.value = null;
    isSelectingCurtainStart = false;
    return;
  }

  selectedCurtainPredefined.value = null;
  if (isSelectingCurtainStart) {
    selectedCurtainStart.value = i;
    selectedCurtainStop.value = null;
    isSelectingCurtainStart = false;
  }
  else {
    selectedCurtainStop.value = i;
    isSelectingCurtainStart = true;
  }
  instance?.proxy?.$forceUpdate();
}

/*
const curtainStart = computed({
  get() {
    if (!selectedCurtainStart.value) {
      return selectedCurtainStop.value;
    }
    if (!selectedCurtainStop.value) {
      return selectedCurtainStart.value;
    }
    if (selectedCurtainStart.value > selectedCurtainStop.value) {
      return selectedCurtainStop.value;
    }
    return selectedCurtainStart.value;
  }
});
*/

function isCurtainSelected(i) {
  if (typeof i == "object") {
    return i == selectedCurtainPredefined.value;
  }

  let start = selectedCurtainStart.value;
  let stop = selectedCurtainStop.value;
  if (!start) {
    return false;
  }
  if (!stop) {
    return i == start;
  }
  return (start <= i && i <= stop) || (stop <= i && i <= start);
}


const selectedOutputWindow = ref(null);

function selectOutputWindow(i) {
  selectedOutputWindow.value = i;
  instance?.proxy?.$forceUpdate();
}

function isOutputWindowSelected(i) {
  return i == selectedOutputWindow.value;
}

const predefinedCurtainPositions = ref([
  { name: "Leva 1/2", value: 21 },
  { name: "Desna 1/2", value: 22 },
  { name: "Prva 1/3", value: 31 },
  { name: "Srednja 1/3", value: 32 },
  { name: "Zadnja 1/3", value: 33 },
  { name: "100%", value: 100 },
]);



const m280DriveStatus = ref("idle"); //idle, positioning, delivering, storing
const m280GateStatus = ref("empty"); //empty, full
const m280AxisStatus = ref({
  x: { ok: true, enabled: true, running: false, inPosition: false, homingComplete: true, error: false },
  y: { ok: true, enabled: true, running: false, inPosition: false, homingComplete: true, error: false },
  z: { ok: true, enabled: true, running: false, inPosition: false, homingComplete: true, error: false }
});
const m280TrayNumber = ref(47);

const abyTx = ref();
const abyRx = ref();

const abyTxCounter = ref();
const abyRxCounter = ref();

let abyTxTimer = null;
let abyRxTimer = null;

function toHexString(byteArray) {
  return Array.from(byteArray, function(byte) {
    return ('0' + (byte & 0xFF).toString(16)).slice(-2);
  }).join('')
}

function initMQTT() {
  mqtt.ready(() => {
  //mqtt.init(); //Already performed in App.vue
    mqtt.on('connect', () => {
      //responses.value.push({ color: "yellow", topic: "Socket", payload: "connected" });
    });
    mqtt.on('disconnect', () => {
      //responses.value.push({ color: "red", topic: "Socket", payload: "disconnected" });
    });
    mqtt.on('received', (data) => {
      //console.log("received", data);

      try {
        if (data.topic == "hcu/cabinets/" + mac.value + "/debug") {
          let obj = data.payload;
          //console.log("debug", obj)

          let now = new Date();
          if (obj && obj.type == "io") {
            let elapsed = obj.elapsed > 0 ? `+${obj.elapsed}ms` : null;
            appendLog({
              text: toHexString(obj.payload).substring(0, 36) + "...",
              time: new Date(obj.timestamp).toISOString().split("T")[1].replace("Z", ""),
              type: obj.dir == "in" ? (obj.data.status == "idle" ? "warning" : "success") : "command",
              direction: obj.dir == "in" ? "inbound" : "outbound",
              elapsed: elapsed,
            });
            appendLog({
              text: obj.dir == "in" ? `Status change to ${obj.data.status}` : `Set action ${obj.data.action}`,
              time: new Date(obj.timestamp).toISOString().split("T")[1].replace("Z", ""),
              type: obj.dir == "in" ? (obj.data.status == "idle" ? "warning" : "success") : "command",
              direction: obj.dir == "in" ? "inbound" : "outbound",
              elapsed: elapsed,
            });

            switch (obj.descriptor) {
              case "abyTx":
                abyTx.value = obj.payload;
                abyTxCounter.value = abyTx.value[24];
                clearInterval(abyTxTimer);
                abyTxTimer = setInterval(() => {
                  if (abyTx.value) {
                    abyTxCounter.value = (abyTxCounter.value + 1) % 256;
                  }
                }, 100);
                break;

              case "abyRx":
                abyRx.value = obj.payload;
                abyRxCounter.value = abyRx.value[24];
                clearInterval(abyRxTimer);
                abyRxTimer = setInterval(() => {
                  if (abyRx.value) {
                    abyRxCounter.value = (abyRxCounter.value + 1) % 256;
                  }
                }, 100);
                break;
            }
          }

        }
      }
      catch (err) {
        console.error(err);
      }

    });
  });
}

function appendLog(record) {
  logRecords.value.push(record);
  if (lastCommandRaw) {
    recentRecords.value.push(record);
  }
}

//const choice = ref('protocol');
const choice = ref('variables');

function setChoice(value) {
  choice.value = value;
}

watch(choice, () => {
  calculateEventLogHeight();
});

const recentRecords = ref([
  // { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
  // { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
  // { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
  // { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
  // { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
  // { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
]);


const command = ref("");

function invokeCommand() {
  if (command.value.trim().length > 0) {
    recentRecords.value = [ ];

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);

    let cmd = command.value;
    if (cmd.toLowerCase().startsWith("set ")) {
      cmd = cmd.toUpperCase();
    }

    cabinet.invokeCommand(cmd);
    
    lastCommandRaw = cmd;
    lastCommandTime = now;

    appendLog({ text: cmd, type: "command", direction: "outbound", time: time });
    command.value = "";
  }
}

function clearRecentLogs() {
  recentRecords.value = [ ];
  command.value = "";
}
</script>

<template>
  <div class="relative">
    <!-- <div><span class="text-lg font-bold dark:text-white">Omara z roleto</span> <span class="text-md text-gray-400 ml-3">150 predalov z vložki</span></div> -->

    <div class="">
      <!-- Top right corner -->
      <div class="flex flex-row justify-between space-x-2 mt-4">

        <div class="relative dark:text-white">
          Miniregalna <span class="font-bold" v-if="props.cabinet && props.cabinet.model">{{ props.cabinet.model.toUpperCase() }}</span>
          <Tag color="blue" class="font-mono ml-2 relative -top-0.5" v-if="mac">{{ mac }}</Tag>
          <Tag color="purple" class="font-mono ml-2 relative -top-0.5" v-if="ip">{{ ip }}</Tag>
        </div>

      </div>
    </div>


    <!-- Top right corner -->
    <div class="flex flex-row justify-items-strech space-x-2 w-full mt-4" v-if="false">

      <!-- Tray -->
      <Label class="label mr-1">Trenutni&nbsp;predal</Label>
      <TextBox v-model="currentTrayNumber" placeholder="--" :readonly="true" style="width: 50px; text-align: center; margin-right: 20px;"></TextBox>

      <!-- Curtain -->
      <Label class="label mr-1">Pozicija&nbsp;zavese</Label>
      <TextBox v-model="currentCurtainPosition" placeholder="--" :readonly="true" style="width: 90px; text-align: center; margin-right: 20px;"></TextBox>

      <!-- Curtain -->
      <!-- <Label class="label mr-1">Stanje pogona</Label>
      <TextBox v-model="currentCurtainPosition" placeholder="idle" :readonly="true" style="width: 90px; text-align: center; margin-right: 20px;"></TextBox> -->

      <!-- Action button -->
      <!-- <Button @click="reset">Reset</Button> -->

    </div>

    <!-- Grid -->
    <div class="w-full mt-4 mb-2">
      <div class="">

        <!-- Row and column -->
        <!--<div class="" v-if="false">
          <div class="text-md font-bold dark:text-white">Vrsta</div>
          <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
            <a href="#" v-for="i in 10" @click="selectRow(i)" class="button" :class="{ 'button-selected': i == selectedRow }">
              <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ alphabet[i - 1] }}</h5>
            </a>
          </div>
          <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
            <a href="#" v-for="i in 4" @click="selectRow(i + 10)" class="button" :class="{ 'button-selected': i + 10 == selectedRow }">
              <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ alphabet[i + 10 - 1] }}</h5>
            </a>
          </div>

          <div class="text-md font-bold dark:text-white mt-8">Stolpec</div>
          <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
            <a href="#" v-for="i in 10" @click="selectColumn(i)" class="button" :class="{ 'button-selected': i == selectedColumn }">
              <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ i }}</h5>
            </a>
          </div>
          <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
            <a href="#" v-for="i in 1" @click="selectColumn(i + 10)" class="button" :class="{ 'button-selected': i + 10 == selectedColumn }">
              <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ i + 10 }}</h5>
            </a>
          </div>
        </div>-->

        <!-- Insert position and length -->
        <div class="text-md font-bold dark:text-white mt-8">{{ $t('service.curtain_position_manual') }}</div>
        <div class="flex flex-row gap-2 w-full mt-2 mb-2">
          <a href="#" v-for="i in 10" @click.prevent="selectCurtain(i)" class="button aspect-square w-16" :class="{ 'button-selected': isCurtainSelected(i) }">
            <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">V<span class="text-sm">{{ i }}</span></h5>
          </a>
        </div>

        <!-- Insert position and length -->
        <div class="text-md font-bold dark:text-white mt-8">{{ $t('service.curtain_position_predefined') }}</div>
        <div class="flex flex-row gap-2 w-full mt-2 mb-2">
          <a href="#" v-for="predefined in predefinedCurtainPositions" @click.prevent="selectCurtain(predefined)" class="button px-3 py-2" :class="{ 'button-selected': isCurtainSelected(predefined) }">
            <h5 class="text-md font-bold tracking-tight text-gray-900 dark:text-white">{{ predefined.name }}</h5>
          </a>
        </div>

        <div class="flex flex-row gap-8">
          <!-- Numpad -->
          <div class="mt-8 w-1/2" style="max-width: 280px;">
            <div class="text-md font-bold dark:text-white">{{ $t('service.tray_select_label') }}</div>
            <KeyboardNumpad v-model="trayNumber" :masked="false" :iconic="false" :readonly="true" :max-length="3" :min-value="1" :max-value="280" @confirm="submit()" />
          </div>

          <!-- Right half -->
          <div class="w-1/2">

            <!-- Select output window (aka gate) -->
            <div class="text-md font-bold dark:text-white mt-8">{{ $t('service.output_window') }}</div>
            <div class="flex flex-row gap-2 w-full grid-cols-2 mt-1 mb-2">
              <a href="#" v-for="i in 3" @click="selectOutputWindow(i - 1)" class="button w-16 aspect-square" :class="{ 'button-selected': isOutputWindowSelected(i - 1) }">
                <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ i - 1 }}</h5>
              </a>
            </div>

            <!-- Commands -->
            <div class="text-md font-bold dark:text-white mt-8">{{ $t('service.commands_label') }}</div>
            <div class="grid gap-2 w-full grid-cols-2 mt-2 mb-2">
              <Button type="primary" left-icon="arrow-up" @click="openTray()">{{ $t('service.tray_open') }}</Button>
              <Button type="primary" left-icon="arrow-down" @click="closeTray()">{{ $t('service.tray_store') }}</Button>
              <Button type="success" left-icon="rotate" @click="goToRef()">{{ $t('service.reference') }}</Button>
              <Button type="danger" @click="stopAction()">{{ $t('service.stop') }}</Button>
            </div>

            <!-- Status and indicators -->
            <div class="mt-8 mb-4">
              <div class="text-md font-bold dark:text-white mb-2">{{ $t('service.status_label') }}</div>
              <div class="grid grid-cols-3 gap-2"><!-- grid-cols-5 -->
                <span class="tag col-span-3">
                  {{ $t('service.drive_status') }}: <span class="text-blue-500" v-if="movementStatus">{{ movementStatus }}</span><span class="text-blue-500" v-else>--</span>
                </span>
                <span class="tag col-span-2" v-if="false">
                  State: <span class="text-blue-500" v-if="internalState">{{ internalState }}</span><span class="text-blue-500" v-else>--</span>
                </span>
              </div>

              <div class="grid grid-cols-4 gap-2 mt-2" v-if="false">
                <span class="tag tag-success" v-if="isReferenced === true">
                  REF_OK
                </span>
                <span class="tag tag-danger" v-else-if="isReferenced === false">
                  NOT_REF
                </span>
                <span class="tag tag-idle" v-else>
                  REF
                </span>

                <span class="tag" :class="{ 'tag-idle': !isCurtainAlarm, 'tag-danger': isCurtainAlarm }">
                  ROLO
                </span>
                <span class="tag" :class="{ 'tag-idle': !isHaltAlarm, 'tag-danger': isHaltAlarm }">
                  HALT
                </span>
                <span class="tag" :class="{ 'tag-idle': !isDisruptAlarm, 'tag-danger': isDisruptAlarm }">
                  DISRUPT
                </span>
              </div>

              <div class="grid grid-cols-3 gap-2 mt-2" v-if="motorStatus">
                <template v-for="(motor, i) in motors">
                  <span class="tag tag-success" v-if="motorStatus[motor] === 'OK'">
                    MOTOR_{{ motor }}
                  </span>
                  <!-- <span class="tag tag-danger" v-else-if="motorStatus[motor] === '???'">
                    MOTOR_{{ motor }}
                  </span> -->
                  <span class="tag tag-idle" v-else>
                    MOTOR_{{ motor }}
                  </span>
                </template>
              </div>

            </div>
            <!-- End of status -->
            
          </div>
        </div>

      </div>
      
      

      <!-- Axis indicators -->
      <div class="mt-8 mb-4 grid grid-cols-2" v-if="false">
        <div>
          <div class="text-md font-bold dark:text-white mb-2">Indikatorji osi</div>
          <div v-for="axis in ['x','y','z','l','r','p']">
            <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200" v-if="m280AxisStatus && m280AxisStatus[axis]">
              <span class="text-bold mr-2 w-2">{{ axis.toUpperCase() }}</span>
              <i class="fa fa-regular fa-circle-check" :class="{ 'text-green-500': m280AxisStatus[axis].ok, 'text-gray-300': !m280AxisStatus[axis].ok }" title="COM OK"></i>
              <i class="fa fa-power-off" :class="{ 'text-green-500': m280AxisStatus[axis].enabled, 'text-gray-300': !m280AxisStatus[axis].enabled }" title="Enabled"></i>
              <i class="fa fa-spinner" :class="{ 'text-purple-500': m280AxisStatus[axis].running, 'text-gray-300': !m280AxisStatus[axis].running }" title="Running"></i>
              <i class="fa fa-crosshairs" :class="{ 'text-blue-500': m280AxisStatus[axis].inPosition, 'text-gray-300': !m280AxisStatus[axis].inPosition }" title="In position"></i>
              <i class="fa fa-house" :class="{ 'text-blue-500': m280AxisStatus[axis].homingComplete, 'text-gray-300': !m280AxisStatus[axis].homingComplete }" title="Homing complete"></i>
              <i class="fa fa-regular fa-circle-xmark" :class="{ 'text-red-500': m280AxisStatus[axis].error, 'text-gray-300': !m280AxisStatus[axis].error }" title="Error"></i>
            </span>
          </div>
        </div>
        <div>
          <div class="text-md font-bold dark:text-white mb-2">Stanje krmilnika</div>
          <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            Stanje pogona: <span class="text-blue-500">idle|positioning|delivering|storing</span>
          </span><br />
          <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            Stanje izhodnega okna: <span class="text-blue-500">polno|prazno</span>
          </span><br />
          <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            Čaka na ukaz pozicioniranja
          </span><br />
          <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            Čaka na ukaz zavese
          </span>
        </div>
      </div>

      <!-- Logger -->
      <!-- <div class="mt-8 pb-4" style="height: 100%;">
        <div class="text-md font-bold dark:text-white mt-8 mb-2">Sporočila</div>
        <EventLog :records="logRecords">
          <template #empty>
            {{ $t('service.no_logs') }}
          </template>
        </EventLog>
      </div> -->

      <!-- Logger -->
      <div class="w-full h-full mt-8 mb-10">

        <div class="tab-control mb-4">
          <button class="tab-button" :class="{ 'tab-active': choice == 'protocol' }" @click="setChoice('protocol')">{{ $t('service.tab_protocol') }}</button>
          <button class="tab-button" :class="{ 'tab-active': choice == 'variables' }" @click="setChoice('variables')">{{ $t('service.tab_variables') }}</button>
        </div>

        <EventLog id="eventLog" ref="eventLogElement" :records="logRecords" :style="'height: ' + eventLogHeight + 'px'" v-if="choice == 'protocol'">
          <template #empty>
            {{ $t('service.no_logs') }}
          </template>
        </EventLog>

        <pre class="px-3 py-3 mb-10 border rounded bg-amber-100 text-amber-700 text-xs font-mono" v-if="choice == 'status'">{{ status }}</pre>

        <div class="w-full" v-if="choice == 'variables'">
          <div class="grid grid-cols-2 space-x-2">
            <div class="border rounded-md px-4 py-3 bg-white font-mono">
              <h3 class="text-blue-700">{{ $t('service.commands_rpi_plc') }}</h3>
              <div v-for="(x, i) in abyRx" v-if="abyRx?.length">
                <span v-if="9 <= i && i <= 24">abyRx<span class="text-blue-700">[</span><span class="text-amber-600">{{ i }}</span><span class="text-blue-700">]</span> = <span class="text-blue-600"><span v-if="i != 24">{{ x }}</span><span v-else class="text-red-600">{{ abyRxCounter }}</span></span></span>
                <span v-else class="text-gray-400">abyRx[{{ i }}] = {{ x }}</span>
              </div>
              <div v-else class="text-gray-400">
                {{ $t('generic.empty') }}
              </div>
            </div>
            <div class="border rounded-md px-4 py-3 bg-white font-mono">
              <h3 class="text-green-700">{{ $t('service.responses_plc_rpi') }}</h3>
              <div v-for="(x, i) in abyTx" v-if="abyTx?.length">
                <span v-if="9 <= i && i <= 24">abyTx<span class="text-blue-700">[</span><span class="text-amber-600">{{ i }}</span><span class="text-blue-700">]</span> = <span class="text-green-600"><span v-if="i != 24">{{ x }}</span><span v-else class="text-red-600">{{ abyTxCounter }}</span></span></span>
                <span v-else class="text-gray-400">abyTx[{{ i }}] = {{ x }}</span>
              </div>
              <div v-else class="text-gray-400">
                {{ $t('generic.empty') }}
              </div>
            </div>
          </div>
        </div>

      </div>
      
    </div>

    <div class="distancer" style="height: 10px;">
    </div>

  </div>
</template>

<style scoped>
.tag {
  @apply inline-flex items-center gap-x-1.5 rounded-md px-2 py-1
    text-xs font-medium ring-1 ring-inset
    text-gray-900 ring-gray-200
    dark:text-white dark:ring-gray-400;
}

.tag.tag-idle {
  @apply text-gray-400 bg-gray-100 ring-gray-200
    dark:text-gray-600 dark:bg-gray-900 dark:ring-gray-600;
}

.tag.tag-success {
  @apply text-green-900 bg-green-100 ring-green-500
    dark:text-green-900 dark:bg-green-400 dark:ring-green-900;
}

.tag.tag-warning {
  @apply text-amber-900 bg-amber-100 ring-amber-500;
}

.tag.tag-danger {
  @apply text-red-900 bg-red-100 ring-red-500
    dark:text-red-200 dark:bg-red-600 dark:ring-red-400;
}

.label {
  position: relative;
  top: 5px;
  height: 30px;
  line-height: 30px;
}

.button {
  @apply flex justify-center place-items-center bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.button.button-selected {
  /* border: 2px solid rgba(26,86,219,1); */
  @apply bg-blue-100 border border-blue-500;
}

.button.button-position,
.button.button-position:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}

.badge {
  @apply relative font-mono font-medium mb-1 px-2.5 py-0.5 rounded;
}

.badge.badge-blue {
  @apply bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300;
}

.badge.badge-green {
  @apply bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300;
}

.badge.badge-yellow {
  @apply bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300;
}

.badge.badge-red {
  @apply bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300;
}
</style>
